import { RefObject, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function listFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.tabIndex !== -1,
  );
}

/**
 * Dialog focus management for the inline Add Layer wizard:
 * focus on open, restore on close, Escape to dismiss, Tab cycle within the dialog.
 */
export function useWizardDialogFocus(options: {
  readonly isOpen: boolean;
  readonly isSubmitting: boolean;
  readonly onCancel: () => void;
}): RefObject<HTMLElement> {
  const { isOpen, isSubmitting, onCancel } = options;
  const dialogRef = useRef<HTMLElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const isSubmittingRef = useRef(isSubmitting);
  const onCancelRef = useRef(onCancel);

  isSubmittingRef.current = isSubmitting;
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    previouslyFocusedRef.current = previouslyFocused;

    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    const focusables = listFocusableElements(dialog);
    const initialTarget = focusables[0] ?? dialog;
    initialTarget.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (isSubmittingRef.current) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        onCancelRef.current();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const currentDialog = dialogRef.current;
      if (!currentDialog) {
        return;
      }

      const elements = listFocusableElements(currentDialog);
      if (elements.length === 0) {
        event.preventDefault();
        currentDialog.focus();
        return;
      }

      const first = elements[0]!;
      const last = elements[elements.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !currentDialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last || !currentDialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener('keydown', handleKeyDown);

    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      const restoreTarget = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (restoreTarget && document.contains(restoreTarget)) {
        restoreTarget.focus();
      }
    };
  }, [isOpen]);

  return dialogRef;
}
